import React, { useEffect, useMemo, useState } from 'react';
import { TimelineEntry } from '../Quote/CorrespondenceTimeline';
import MailPreviewFrame, { MailActionPills, MailPreviewModel } from '../Quote/MailPreviewFrame';
import { API_BASE_URL } from '../../utils/apiBase';
import { buildJobHistory, historyMailSummary, itemMailLabel, JobHistoryEnquiry, JobHistoryKind, JobHistoryPayment } from '../../utils/jobHistory';
import { StoredQuote } from '../../utils/quoteRecord';
import { studioWhen } from '../../utils/studioDate';

type Filter = 'all' | JobHistoryKind;

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'change', label: 'Changes' },
  { id: 'message', label: 'Messages' },
  { id: 'money', label: 'Money' },
  { id: 'email', label: 'Email' },
];

interface AccountingAudit {
  at: string;
  by?: string;
  change?: string;
  detail?: string;
}

/**
 * The full record of one shoot: what changed, what was said, what was paid, and what was emailed.
 */
function sendAgainLine(status?: string) {
  if (status === 'sent' || status === 'delivered') return 'Sent again.';
  if (status === 'captured') return 'Kept here. Outbound email is not turned on.';
  if (status === 'not_configured') return 'Email is not set up, so it was not sent.';
  if (status === 'queued') return 'Queued to send.';
  if (status === 'failed') return 'It did not send.';
  return 'Recorded.';
}

const JobHistory = ({
  quote,
  timeline,
  enquiries,
  payments,
  arrangement,
  onSent,
}: {
  quote: StoredQuote | null;
  timeline: TimelineEntry[];
  enquiries: JobHistoryEnquiry[];
  payments: JobHistoryPayment[] | null;
  arrangement?: { id: string; status?: string; amountMinor?: number; note?: string; createdAt?: string } | null;
  onSent?: () => void;
}) => {
  const [filter, setFilter] = useState<Filter>('all');
  const [accounting, setAccounting] = useState<AccountingAudit[]>([]);
  const [sendingId, setSendingId] = useState('');
  const [sendNote, setSendNote] = useState<Record<string, string>>({});
  const [previewBusy, setPreviewBusy] = useState('');
  const [preview, setPreview] = useState<MailPreviewModel | null>(null);

  useEffect(() => {
    if (!quote?.id) {
      setAccounting([]);
      return undefined;
    }
    let active = true;
    fetch(`${API_BASE_URL}/billing/quotes/${quote.id}/accounting`)
      .then(async (response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((body) => {
        if (!active || !body) return;
        setAccounting(Array.isArray(body.audit) ? body.audit : []);
      })
      .catch(() => {
        if (active) setAccounting([]);
      });
    return () => {
      active = false;
    };
  }, [quote?.id, quote?.updatedAt]);

  const entries = useMemo(() => buildJobHistory({
    quote,
    timeline,
    enquiries,
    payments,
    arrangement,
    accounting,
  }), [quote, timeline, enquiries, payments, arrangement, accounting]);
  const mail = useMemo(() => historyMailSummary(entries), [entries]);

  const visible = filter === 'all'
    ? entries
    : filter === 'email'
      ? entries.filter((entry) => entry.kind === 'email' || Boolean(entry.comms?.length))
      : entries.filter((entry) => entry.kind === filter);

  const openPreview = async (noticeId: string, canSend: boolean) => {
    if (!noticeId || previewBusy || sendingId) return;
    setPreviewBusy(noticeId);
    setSendNote((current) => ({ ...current, [noticeId]: '' }));
    try {
      const response = await fetch(`${API_BASE_URL}/studio/notices/${noticeId}/preview`);
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.html) {
        setSendNote((current) => ({ ...current, [noticeId]: body.error || 'The email could not be opened.' }));
        return;
      }
      setPreview({
        id: noticeId,
        subject: body.subject || 'Email',
        to: body.to || '',
        html: body.html,
        canSend,
      });
    } catch {
      setSendNote((current) => ({ ...current, [noticeId]: 'The email could not be opened.' }));
    } finally {
      setPreviewBusy('');
    }
  };

  const sendAgain = async (noticeId: string) => {
    if (!noticeId || sendingId) return;
    setSendingId(noticeId);
    setSendNote((current) => ({ ...current, [noticeId]: '' }));
    try {
      const response = await fetch(`${API_BASE_URL}/studio/notices/${noticeId}/retry`, { method: 'POST' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setSendNote((current) => ({ ...current, [noticeId]: body.error || 'It did not send.' }));
        return;
      }
      setSendNote((current) => ({ ...current, [noticeId]: sendAgainLine(body.status) }));
      onSent?.();
    } catch {
      setSendNote((current) => ({ ...current, [noticeId]: 'It did not send.' }));
    } finally {
      setSendingId('');
    }
  };

  return (
    <section className="studio-panel job-history-panel" data-testid="workspace-history" role="tabpanel" aria-labelledby="shoot-tab-history">
      <h3 className="studio-section-label">Job history</h3>
      <p className="studio-note">Every change, message, payment and email recorded on this shoot. Newest first.</p>
      <p className="job-history-mail-count" data-testid="history-mail-count">{mail.label}</p>
      <div className="job-history-filters" role="group" aria-label="History filter">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={filter === item.id}
            className={filter === item.id ? 'is-active' : ''}
            onClick={() => setFilter(item.id)}
          >
            {item.id === 'email' && mail.recorded ? `${item.label} ${mail.recorded}` : item.label}
          </button>
        ))}
      </div>
      {visible.length === 0 ? (
        <p className="studio-empty">{entries.length === 0 ? 'Nothing has been recorded on this shoot yet.' : 'Nothing in this part of the history.'}</p>
      ) : (
        <ol className="job-history" aria-label="Job history">
          {visible.map((entry) => (
            <li key={entry.id} className="job-history-row" data-kind={entry.kind}>
              <time dateTime={entry.at}>{studioWhen(entry.at)}</time>
              <div>
                <strong>{entry.title}</strong>
                <small>{entry.who}{entry.comms?.length ? ` · ${itemMailLabel(entry.comms)}` : ''}</small>
                {entry.detail ? <p>{entry.detail}</p> : null}
                {entry.comms?.length ? (
                  <ul className="job-history-comms">
                    {entry.comms.map((comm) => (
                      <li key={comm.noticeId}>
                        <span>{comm.label}</span>
                        {comm.detail ? <small>{comm.detail}</small> : null}
                        <MailActionPills
                          noticeId={comm.noticeId}
                          canSend={comm.canSend}
                          busy={previewBusy === comm.noticeId}
                          sending={sendingId === comm.noticeId}
                          onPreview={() => openPreview(comm.noticeId, comm.canSend)}
                          onResend={comm.canSend ? () => sendAgain(comm.noticeId) : undefined}
                        />
                        {sendNote[comm.noticeId] && preview?.id !== comm.noticeId ? <span className="job-history-send-note">{sendNote[comm.noticeId]}</span> : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
      {preview ? (
        <MailPreviewFrame
          preview={preview}
          note={sendNote[preview.id]}
          sending={sendingId === preview.id}
          onSend={preview.canSend ? () => sendAgain(preview.id) : undefined}
          onClose={() => { if (!sendingId) setPreview(null); }}
        />
      ) : null}
    </section>
  );
};

export default JobHistory;
